import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit, ViewChild, Input, Output, EventEmitter, AfterViewInit, signal, ElementRef } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ChunkService } from 'src/app/views/content/chunk/chunk.service';
import { ContentStateService } from 'src/app/views/content/content-state.service';
import { WhiteboardPartComponent } from 'src/app/shared/components/whiteboard/whiteboard-part.component';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { WhiteboardService } from './whiteboard.service';
import { SidenavService } from 'src/app/shared/services/sidenav.service';
import { VideoCallStateService } from 'src/app/shared/components/video-call-drawer-chime/video-call-state.service';
import { ZoomControlComponent } from 'src/app/shared/components/ui/zoom-control/zoom-control.component';
import { ZoomableDirective } from 'src/app/shared/directives/zoomable.directive';
import { DynamicOverlayService } from 'src/app/shared/services/dynamic-overlay/dynamic-overlay.service';
import { ColorPaletteOverlayComponent, ColorPaletteOverlayData } from 'src/app/shared/components/ui/color-palette-overlay/color-palette-overlay.component';
import { take, debounceTime } from 'rxjs/operators';
import { ConfirmMenuService } from 'src/app/shared/services/confirm-menu/confirm-menu.service';
import { PostIt } from 'src/app/shared/components/whiteboard/postit.models';
import { DrawItem, Point } from 'src/app/shared/components/whiteboard/whiteboard.models';
import { ChimeService } from 'src/app/shared/components/video-call-drawer-chime/chime.service';
import { Subject, Subscription } from 'rxjs';

@Component({
  selector: 'room-whiteboard',
  templateUrl: './whiteboard.component.html',
  styleUrls: ['./whiteboard.component.scss'],
  standalone: true,
  imports: [
    WhiteboardPartComponent,
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    TranslateModule,
    FormsModule,
    ZoomControlComponent,
    ZoomableDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WhiteboardComponent implements OnInit, AfterViewInit {
  @ViewChild('whiteboardPart') whiteboardPart!: WhiteboardPartComponent;
  @ViewChild('toolbarContainer') toolbarContainer!: ElementRef<HTMLDivElement>;

  @Input() isEditingChunk = false;
  @Input() chunkId?: string;
  @Input() contentId?: string;
  @Input() edition?: any;
  @Input() readOnly = false;
  @Output() close = new EventEmitter<void>();

  showScrollButtons = signal(false);
  private resizeObserver: ResizeObserver | null = null;

  color = '#333333';
  size = 5;
  isActive = false;
  boardID;
  mode: 'pencil' | 'pan' | 'eraser' | 'postit' = 'pencil';
  zoomScale = 1.0;
  postits: PostIt[] = [];

  public sidenavService = inject(SidenavService);
  private videoCallStateService = inject(VideoCallStateService);
  private toast = inject(ToastService);
  private streamingService = inject(WhiteboardService);
  private overlayService = inject(DynamicOverlayService);
  private confirmMenuService = inject(ConfirmMenuService);
  private cdr = inject(ChangeDetectorRef);
  private chimeService = inject(ChimeService);
  private translate = inject(TranslateService);
  private chunkService = inject(ChunkService);
  private contentState = inject(ContentStateService);

  private chimeSubscription: (() => void) | null = null;
  private postItMoveSubject = new Subject<{ id: string; x: number; y: number }>();
  private postItTextChangeSubject = new Subject<{ id: string; text: string }>();
  private autoSaveSubject = new Subject<void>();
  private autoSaveSubscription?: Subscription;
  saveStatus = signal<'saving' | 'saved' | ''>('');

  // Drag-and-drop state for post-its
  private activeDragNote: PostIt | null = null;
  private dragStartX = 0;
  private dragStartY = 0;
  private initialNoteX = 0;
  private initialNoteY = 0;

  private globalMouseMoveListener: ((e: MouseEvent) => void) | null = null;
  private globalMouseUpListener: (() => void) | null = null;
  private globalTouchMoveListener: ((e: TouchEvent) => void) | null = null;
  private globalTouchEndListener: (() => void) | null = null;
 
  openColorPicker(trigger: any): void {
    const data: ColorPaletteOverlayData = {
      colors: [
        '#333333', // Dark Slate
        '#e53e3e', // Red
        '#3182ce', // Blue
        '#38a169', // Green
        '#dd6b20', // Orange
        '#805ad5', // Purple
        '#319795', // Teal
        '#d69e2e', // Yellow
        '#4a5568', // Grey
        '#008080'  // Custom Teal
      ]
    };

    const { componentInstance } = this.overlayService.open(trigger._elementRef, ColorPaletteOverlayComponent, {
      hasBackdrop: true,
      data
    });

    componentInstance.colorSelected.pipe(take(1)).subscribe((color) => {
      this.color = color;
      this.overlayService.close();
    });
  }

  constructor() {}

  ngOnInit() {
    this.boardID = Math.random();

    if (this.readOnly) {
      this.mode = 'pan';
    }

    this.postItMoveSubject.pipe(debounceTime(50)).subscribe(({ id, x, y }) => {
      this.broadcastEvent({ type: 'move-postit', id, x, y });
    });

    this.postItTextChangeSubject.pipe(debounceTime(300)).subscribe(({ id, text }) => {
      this.broadcastEvent({ type: 'edit-postit', id, text });
    });

    if (!this.isEditingChunk && this.chimeService.isMeetingActive()) {
      this.chimeSubscription = this.chimeService.subscribeToDataMessage('whiteboard', (msg) => {
        this.handleRemoteWhiteboardEvent(msg);
      });
      this.broadcastEvent({ type: 'request-sync' });
    }

    if (this.isEditingChunk && !this.readOnly) {
      this.autoSaveSubscription = this.autoSaveSubject.pipe(debounceTime(2000)).subscribe(() => {
        this.performAutoSave(false);
      });
    }
  }

  scrollToolbar(direction: 'left' | 'right'): void {
    if (this.toolbarContainer) {
      const scrollAmount = 150;
      this.toolbarContainer.nativeElement.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  }

  checkOverflow(): void {
    if (this.toolbarContainer) {
      const el = this.toolbarContainer.nativeElement;
      const overflow = el.scrollWidth > el.clientWidth;
      this.showScrollButtons.set(overflow);
    }
  }

  ngOnDestroy() {
    this.endDragNote();
    if (this.chimeSubscription) {
      this.chimeSubscription();
    }
    this.postItMoveSubject.complete();
    this.postItTextChangeSubject.complete();
    if (this.saveStatus() === 'saving') {
      this.performAutoSave(false);
    }
    this.autoSaveSubscription?.unsubscribe();
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  ngAfterViewInit() {
    if (this.isEditingChunk && this.edition) {
      try {
        const contentStr = this.edition.content;
        const data = typeof contentStr === 'string' ? JSON.parse(contentStr) : contentStr;
        if (data) {
          if (data.drawHistory && this.whiteboardPart) {
            this.whiteboardPart.drawHistory = data.drawHistory;
            this.whiteboardPart.redrawAll();
          }
          if (data.postits) {
            this.postits = data.postits;
            this.cdr.markForCheck();
          }
        }
      } catch (e) {
        console.error('Failed to parse whiteboard chunk content', e);
      }
    }

    if (typeof window !== 'undefined' && 'ResizeObserver' in window && this.toolbarContainer) {
      this.resizeObserver = new ResizeObserver(() => {
        this.checkOverflow();
        this.cdr.markForCheck();
      });
      this.resizeObserver.observe(this.toolbarContainer.nativeElement);
    }
  }

  closeWhiteboard() {
    if (this.isEditingChunk) {
      if (this.saveStatus() === 'saving') {
        this.performAutoSave(true);
      } else {
        this.close.emit();
      }
    } else {
      this.videoCallStateService.closeWhiteboard();
    }
  }

  onClear() {
    this.toast.snackbar.open('Tableau effacé!', 'Close', {
      duration: 2000,
    });
  }

  onSave() {
    this.toast.snackbar.open('Tableau téléchargé!', 'Close', {
      duration: 2000,
    });
  }
  onImageAded() {
    this.toast.snackbar.open('Image ajoutée!', 'Close', {
      duration: 2000,
    });
  }

  erase(trigger: any) {
    this.confirmMenuService.confirm(
      trigger._elementRef || trigger,
      'Global.Clear',
      'delete',
      'warn',
      'VideoCall.Whiteboard.ClearConfirmTitle',
      'VideoCall.Whiteboard.ClearConfirmMessage'
    ).subscribe(() => {
      this.streamingService.passBoardEvent({
        boardID: this.boardID,
        type: 'erase',
      });
      this.broadcastEvent({ type: 'erase' });
      this.postits = [];
      this.cdr.markForCheck();
      this.onClear();
      this.triggerAutoSave();
    });
  }
  setSize(size) {
    this.size = size;
    this.isActive = false;
  }
  undo() {
    this.streamingService.passBoardEvent({
      boardID: this.boardID,
      type: 'undo',
    });
    this.broadcastEvent({ type: 'undo' });
    this.triggerAutoSave();
  }
  redo() {
    this.streamingService.passBoardEvent({
      boardID: this.boardID,
      type: 'redo',
    });
    this.broadcastEvent({ type: 'redo' });
    this.triggerAutoSave();
  }
  save() {
    if (this.isEditingChunk) {
      this.saveChunkData();
    } else {
      this.streamingService.passBoardEvent({
        boardID: this.boardID,
        type: 'save',
      });
      this.onSave();
    }
  }

  triggerAutoSave() {
    if (!this.isEditingChunk || this.readOnly) return;
    this.saveStatus.set('saving');
    this.cdr.markForCheck();
    this.autoSaveSubject.next();
  }

  performAutoSave(closeAfterSave = false) {
    if (!this.isEditingChunk || !this.chunkId || !this.edition || !this.whiteboardPart || this.readOnly) {
      if (closeAfterSave) {
        this.closeWhiteboard();
      }
      return;
    }

    const data = {
      drawHistory: this.whiteboardPart.drawHistory,
      postits: this.postits,
    };

    const editData = {
      id: this.chunkId,
      edition: this.edition,
      content: JSON.stringify(data),
      contentID: this.contentId || this.contentState.contentId,
      language: this.translate.currentLang,
    };

    this.chunkService.editChunk(editData).subscribe({
      next: () => {
        this.saveStatus.set('saved');
        this.cdr.markForCheck();
        if (closeAfterSave) {
          this.closeWhiteboard();
        }
      },
      error: (err) => {
        console.error('Failed to auto-save whiteboard chunk', err);
        this.saveStatus.set('');
        this.cdr.markForCheck();
        if (closeAfterSave) {
          this.closeWhiteboard();
        }
      },
    });
  }

  saveChunkData() {
    this.performAutoSave(true);
  }
  addImage(fileInput) {
    const file = fileInput.files[0];
    const reader = new FileReader();

    reader.onloadend = () => {
      /*       this.whiteboardService.addImage(reader.result);
       */ fileInput.value = '';
    };

    if (file) {
      reader.readAsDataURL(file);
    }
  }

  onZoomContainerClick(event: MouseEvent, container: HTMLElement, zoom: ZoomableDirective) {
    if (this.mode !== 'postit') return;

    const rect = container.getBoundingClientRect();
    const x = (event.clientX - rect.left - zoom.panX()) / zoom.zoomScale();
    const y = (event.clientY - rect.top - zoom.panY()) / zoom.zoomScale();

    // Map selected brush color to a beautiful light pastel sticky note variant
    let noteColor = '#fef3c7'; // Default light yellow (Tailwind amber-100)
    if (this.color === '#e53e3e') noteColor = '#fee2e2'; // light red
    else if (this.color === '#3182ce') noteColor = '#dbeafe'; // light blue
    else if (this.color === '#38a169') noteColor = '#dcfce7'; // light green
    else if (this.color === '#dd6b20') noteColor = '#ffedd5'; // light orange
    else if (this.color === '#805ad5') noteColor = '#f3e8ff'; // light purple
    else if (this.color === '#319795') noteColor = '#ccfbf1'; // light teal
    else if (this.color === '#d69e2e') noteColor = '#fef9c3'; // light yellow

    const newNote = {
      id: Math.random().toString(36).substring(2, 9),
      text: '',
      x: x - 75,
      y: y - 75,
      color: noteColor
    };

    this.postits = [
      ...this.postits,
      newNote
    ];

    this.broadcastEvent({ type: 'add-postit', note: newNote });
    this.mode = 'pencil'; // Switch back to pencil
    this.triggerAutoSave();
  }

  deletePostIt(id: string) {
    this.postits = this.postits.filter((note) => note.id !== id);
    this.broadcastEvent({ type: 'delete-postit', id });
    this.triggerAutoSave();
  }

  startDragNote(event: MouseEvent, note: PostIt, zoomScale: number): void {
    if (event.button !== 0) return; // Only left-click
    
    event.preventDefault();
    event.stopPropagation();

    this.activeDragNote = note;
    this.dragStartX = event.clientX;
    this.dragStartY = event.clientY;
    this.initialNoteX = note.x;
    this.initialNoteY = note.y;

    this.globalMouseMoveListener = (e: MouseEvent) => this.onDragNoteMove(e, zoomScale);
    this.globalMouseUpListener = () => this.endDragNote();

    document.addEventListener('mousemove', this.globalMouseMoveListener);
    document.addEventListener('mouseup', this.globalMouseUpListener);
  }

  startDragNoteTouch(event: TouchEvent, note: PostIt, zoomScale: number): void {
    if (event.touches.length !== 1) return;
    
    event.stopPropagation();

    const touch = event.touches[0];
    this.activeDragNote = note;
    this.dragStartX = touch.clientX;
    this.dragStartY = touch.clientY;
    this.initialNoteX = note.x;
    this.initialNoteY = note.y;

    this.globalTouchMoveListener = (e: TouchEvent) => this.onDragNoteTouchMove(e, zoomScale);
    this.globalTouchEndListener = () => this.endDragNote();

    document.addEventListener('touchmove', this.globalTouchMoveListener, { passive: false });
    document.addEventListener('touchend', this.globalTouchEndListener);
  }

  private onDragNoteMove(event: MouseEvent, zoomScale: number): void {
    if (!this.activeDragNote) return;

    const deltaX = (event.clientX - this.dragStartX) / zoomScale;
    const deltaY = (event.clientY - this.dragStartY) / zoomScale;

    this.activeDragNote.x = this.initialNoteX + deltaX;
    this.activeDragNote.y = this.initialNoteY + deltaY;

    this.cdr.markForCheck();
    this.postItMoveSubject.next({ id: this.activeDragNote.id, x: this.activeDragNote.x, y: this.activeDragNote.y });
  }

  private onDragNoteTouchMove(event: TouchEvent, zoomScale: number): void {
    if (!this.activeDragNote || event.touches.length !== 1) return;
    
    event.preventDefault(); // Prevent page scrolling
    const touch = event.touches[0];

    const deltaX = (touch.clientX - this.dragStartX) / zoomScale;
    const deltaY = (touch.clientY - this.dragStartY) / zoomScale;

    this.activeDragNote.x = this.initialNoteX + deltaX;
    this.activeDragNote.y = this.initialNoteY + deltaY;

    this.cdr.markForCheck();
    this.postItMoveSubject.next({ id: this.activeDragNote.id, x: this.activeDragNote.x, y: this.activeDragNote.y });
  }

  private endDragNote(): void {
    if (this.activeDragNote) {
      this.broadcastEvent({ type: 'move-postit', id: this.activeDragNote.id, x: this.activeDragNote.x, y: this.activeDragNote.y });
      this.triggerAutoSave();
    }

    this.activeDragNote = null;

    if (this.globalMouseMoveListener) {
      document.removeEventListener('mousemove', this.globalMouseMoveListener);
      this.globalMouseMoveListener = null;
    }
    if (this.globalMouseUpListener) {
      document.removeEventListener('mouseup', this.globalMouseUpListener);
      this.globalMouseUpListener = null;
    }
    if (this.globalTouchMoveListener) {
      document.removeEventListener('touchmove', this.globalTouchMoveListener);
      this.globalTouchMoveListener = null;
    }
    if (this.globalTouchEndListener) {
      document.removeEventListener('touchend', this.globalTouchEndListener);
      this.globalTouchEndListener = null;
    }
  }

  onLocalStrokeAdded(stroke: DrawItem) {
    this.broadcastEvent({ type: 'stroke', stroke });
    this.triggerAutoSave();
  }

  onPostItTextChanged(note: PostIt) {
    this.postItTextChangeSubject.next({ id: note.id, text: note.text });
    this.triggerAutoSave();
  }

  private compressPoints(points: Point[]): number[] {
    const flat: number[] = [];
    for (const p of points) {
      flat.push(p.x, p.y);
    }
    return flat;
  }

  private decompressPoints(flat: number[]): Point[] {
    const points: Point[] = [];
    for (let i = 0; i < flat.length; i += 2) {
      points.push({ x: flat[i], y: flat[i + 1] });
    }
    return points;
  }

  private broadcastEvent(event: any): void {
    if (this.isEditingChunk) return;
    if (this.chimeService.isMeetingActive()) {
      let payload = event;
      if (event.type === 'stroke' && event.stroke) {
        payload = {
          ...event,
          stroke: {
            ...event.stroke,
            points: this.compressPoints(event.stroke.points),
          },
        };
      } else if (event.type === 'sync-state' && event.drawHistory) {
        payload = {
          ...event,
          drawHistory: event.drawHistory.map((stroke: any) => ({
            ...stroke,
            points: this.compressPoints(stroke.points),
          })),
        };
      }
      this.chimeService.sendDataMessage('whiteboard', payload);
    }
  }

  private handleRemoteWhiteboardEvent(msg: any): void {
    if (!msg || !msg.type) return;

    switch (msg.type) {
      case 'request-sync':
        if (this.whiteboardPart || this.postits.length > 0) {
          let delay = 0;
          if (this.whiteboardPart && this.whiteboardPart.drawHistory.length > 0) {
            const activeStrokes = this.whiteboardPart.drawHistory.slice(
              0,
              this.whiteboardPart.drawHistory.length - this.whiteboardPart.undoCount
            );
            activeStrokes.forEach((stroke) => {
              setTimeout(() => {
                this.broadcastEvent({ type: 'stroke', stroke });
              }, delay);
              delay += 20; // 20ms delay to avoid rate limiting
            });
          }
          this.postits.forEach((note) => {
            setTimeout(() => {
              this.broadcastEvent({ type: 'add-postit', note });
            }, delay);
            delay += 20;
          });
        }
        break;
      case 'sync-state':
        if (msg.drawHistory && this.whiteboardPart) {
          const decompressedHistory = msg.drawHistory.map((stroke: any) => ({
            ...stroke,
            points: Array.isArray(stroke.points[0]) || typeof stroke.points[0] === 'object'
              ? stroke.points
              : this.decompressPoints(stroke.points),
          }));
          this.streamingService.passBoardEvent({
            boardID: this.boardID,
            type: 'sync-history',
            history: decompressedHistory,
          });
        }
        if (msg.postits) {
          this.postits = msg.postits;
          this.cdr.markForCheck();
        }
        break;
      case 'stroke':
        if (msg.stroke && this.whiteboardPart) {
          const decompressedStroke = {
            ...msg.stroke,
            points: Array.isArray(msg.stroke.points[0]) || typeof msg.stroke.points[0] === 'object'
              ? msg.stroke.points
              : this.decompressPoints(msg.stroke.points),
          };
          this.streamingService.passBoardEvent({
            boardID: this.boardID,
            type: 'stroke',
            stroke: decompressedStroke,
          });
        }
        break;
      case 'erase':
        this.streamingService.passBoardEvent({
          boardID: this.boardID,
          type: 'erase',
        });
        this.postits = [];
        this.onClear();
        this.cdr.markForCheck();
        break;
      case 'undo':
        this.streamingService.passBoardEvent({
          boardID: this.boardID,
          type: 'undo',
        });
        this.cdr.markForCheck();
        break;
      case 'redo':
        this.streamingService.passBoardEvent({
          boardID: this.boardID,
          type: 'redo',
        });
        this.cdr.markForCheck();
        break;
      case 'add-postit':
        if (msg.note) {
          if (!this.postits.some((n) => n.id === msg.note.id)) {
            this.postits = [...this.postits, msg.note];
            this.cdr.markForCheck();
          }
        }
        break;
      case 'move-postit':
        if (msg.id) {
          const note = this.postits.find((n) => n.id === msg.id);
          if (note) {
            note.x = msg.x;
            note.y = msg.y;
            this.cdr.markForCheck();
          }
        }
        break;
      case 'edit-postit':
        if (msg.id) {
          const note = this.postits.find((n) => n.id === msg.id);
          if (note) {
            note.text = msg.text;
            this.cdr.markForCheck();
          }
        }
        break;
      case 'delete-postit':
        if (msg.id) {
          this.postits = this.postits.filter((n) => n.id !== msg.id);
          this.cdr.markForCheck();
        }
        break;
    }
  }
}
